'use client'

import { BellIcon } from 'lucide-react'
import React, { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { GOALS, reachGoal } from '@/analytics/metrika'

type Props = {
  productID: number
  variantID?: number
  /** Что именно ждём — для заголовка диалога: «Футболка UtroClub — M — Белый» */
  title: string
}

/** Кнопка «Сообщить о поступлении» для распроданного товара или варианта. */
export const RestockDialog: React.FC<Props> = ({ productID, variantID, title }) => {
  const [open, setOpen] = useState(false)
  const [contact, setContact] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle')
  const [message, setMessage] = useState('')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setState('sending')
    try {
      const res = await fetch('/api/restock/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productID, variantID, contact }),
      })
      const data = (await res.json()) as { message?: string; duplicate?: boolean }
      if (!res.ok) {
        setMessage(data.message ?? 'Не получилось сохранить заявку.')
        setState('error')
        return
      }
      reachGoal(GOALS.restockRequested)
      setMessage(data.duplicate ? 'Вы уже в списке — напишем, как появится.' : 'Записали. Напишем, как только появится.')
      setState('done')
    } catch {
      setMessage('Проверьте связь и попробуйте ещё раз.')
      setState('error')
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v)
        if (!v) {
          setState('idle')
          setMessage('')
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="lg" className="w-full sm:w-auto sm:min-w-56">
          <BellIcon className="w-4 h-4 mr-1" />
          Сообщить о поступлении
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Сообщить о поступлении</DialogTitle>
          <DialogDescription>{title}</DialogDescription>
        </DialogHeader>
        {state === 'done' ? (
          <p className="text-sm">{message}</p>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="restock-contact">Телефон или Telegram</Label>
              <Input
                id="restock-contact"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="@username или +7 900 000-00-00"
                autoFocus
                required
                minLength={5}
              />
            </div>
            {state === 'error' && (
              <p role="alert" className="text-sm text-destructive">
                {message}
              </p>
            )}
            <Button type="submit" disabled={state === 'sending'}>
              {state === 'sending' ? 'Отправляем…' : 'Напишите мне'}
            </Button>
            <p className="text-xs text-primary/60">Контакт используем только чтобы сообщить о поступлении этого товара.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
